import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { D, designError, downloadBlob, fmtDateTime, label, useDesignMe } from "@/lib/designApi";
import { ErrorList, Field, NoAccess, PageHeader, Section, SimpleSelect } from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileSpreadsheet, Play, Upload } from "lucide-react";
import { toast } from "sonner";

function Reports() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("items");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`${D}/reports`).then((r) => setList(r.data)).catch(() => {}); }, []);
  const run = () => {
    setErr("");
    api.get(`${D}/reports/${name}`, { params: { date_from: from, date_to: to } }).then((r) => setData(r.data)).catch((e) => { setData(null); setErr(designError(e)); });
  };
  useEffect(run, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-end flex-wrap">
        <Field label="Report"><div className="w-72 max-md:w-full"><SimpleSelect value={name} onChange={setName} options={list.map((r) => ({ value: r.key, label: r.label }))} testid="report-select" /></div></Field>
        <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button variant="outline" onClick={run}><Play className="w-4 h-4 mr-1" />Run</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => downloadBlob(`${D}/reports/${name}`, `design-${name}.xlsx`, { format: "xlsx", date_from: from, date_to: to }).catch((e) => toast.error(designError(e)))} data-testid="report-export">
          <FileSpreadsheet className="w-4 h-4 mr-1" />Export Excel
        </Button>
      </div>
      <ErrorList errors={err ? [err] : []} />
      {data && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-auto max-h-[65vh]">
          <table className="w-full text-xs whitespace-nowrap" data-testid="report-table">
            <thead className="sticky top-0 bg-white"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b">{data.columns.map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="py-8 text-center text-slate-500">No rows.</td></tr>}
              {data.rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{r.map((v, j) => <td key={j} className="py-1.5 px-2">{Array.isArray(v) ? v.join(", ") : String(v ?? "")}</td>)}</tr>)}
            </tbody>
          </table>
          <div className="text-xs text-slate-500 p-2">{data.rows.length} row(s)</div>
        </div>
      )}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ entity_type: "", action: "", user_email: "", q: "", date_from: "", date_to: "" });
  const [err, setErr] = useState("");
  const run = () => api.get(`${D}/audit`, { params: f }).then((r) => { setRows(r.data); setErr(""); }).catch((e) => setErr(designError(e)));
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap items-end">
        <Field label="Type"><div className="w-40"><SimpleSelect value={f.entity_type || "all"} onChange={(v) => setF({ ...f, entity_type: v === "all" ? "" : v })}
          options={["all", "items", "documents", "boms", "routings", "standards", "ecns", "ecrs", "requests", "npd", "settings", "import", "reports", "esign"]} /></div></Field>
        <Field label="Action"><div className="w-44 max-md:flex-1 max-md:min-w-[8rem]"><SimpleSelect value={f.action || "all"} onChange={(v) => setF({ ...f, action: v === "all" ? "" : v })}
          options={["all", "create", "edit", "submit", "check", "approve", "reject", "release", "revise", "status_change", "download", "file_upload", "file_remove", "publish_erp", "esign_failed", "settings_change", "role_change", "gate_decision", "implement"]} /></div></Field>
        <Field label="User email"><Input value={f.user_email} onChange={(e) => setF({ ...f, user_email: e.target.value })} /></Field>
        <Field label="Reference"><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} /></Field>
        <Field label="From"><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
        <Field label="To"><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
        <Button variant="outline" onClick={run}>Filter</Button>
        <Button variant="outline" onClick={() => downloadBlob(`${D}/reports/audit`, "design-audit.xlsx", { format: "xlsx", date_from: f.date_from, date_to: f.date_to })}><Download className="w-4 h-4 mr-1" />Export</Button>
      </div>
      <ErrorList errors={err ? [err] : []} />
      <div className="bg-white border border-slate-200 rounded-lg overflow-auto max-h-[65vh]">
        <table className="w-full text-xs" data-testid="audit-table">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b">
            {["When", "Type", "Reference", "Action", "Status", "User", "Role", "IP", "Details"].map((h) => <th key={h} className="py-2 px-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id} className="border-b border-slate-50 align-top">
              <td className="py-1.5 px-2 whitespace-nowrap">{fmtDateTime(a.at)}</td><td className="px-2">{a.entity_type}</td><td className="px-2 mono whitespace-nowrap">{a.ref}</td>
              <td className="px-2 font-medium">{label(a.action)}</td>
              <td className="px-2 whitespace-nowrap">{a.from_status || a.to_status ? `${label(a.from_status) || "—"} → ${label(a.to_status) || "—"}` : ""}</td>
              <td className="px-2">{a.user_email}</td><td className="px-2">{label(a.functional_role)}</td><td className="px-2 mono">{a.ip}</td>
              <td className="px-2 text-slate-500 break-all max-w-md">{JSON.stringify(a.details || {}).slice(0, 220)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">The audit trail is append-only — entries cannot be edited or deleted from the application.</div>
    </div>
  );
}

function ImportPanel({ kind }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [allowSimilar, setAllowSimilar] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      if (kind === "items") fd.append("allow_similar", allowSimilar ? "true" : "false");
      const { data } = await api.post(`${D}/import/${kind === "items" ? "items" : "bom"}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      if (!dryRun) toast.success(kind === "items" ? `${data.created} item(s) created as drafts` : `${data.created} BOM(s) created, ${data.updated} updated`);
    } catch (e) { toast.error(designError(e)); } finally { setBusy(false); }
  };
  return (
    <Section title={kind === "items" ? "Import items" : "Import BOMs"} right={
      <Button size="sm" variant="outline" onClick={() => downloadBlob(`${D}/import/template/${kind}`, `design-${kind}-template.xlsx`)}><Download className="w-3.5 h-3.5 mr-1" />Template</Button>}>
      <div className="flex gap-2 items-center flex-wrap">
        <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); e.target.value = ""; }} />
        <Button variant="outline" onClick={() => ref.current?.click()}><Upload className="w-4 h-4 mr-1" />{file ? file.name : "Choose .xlsx"}</Button>
        {kind === "items" && <label className="text-xs flex items-center gap-2"><Checkbox checked={allowSimilar} onCheckedChange={(v) => setAllowSimilar(!!v)} />Import rows flagged as similar to existing items</label>}
        <Button variant="outline" disabled={!file || busy} onClick={() => go(true)} data-testid={`import-${kind}-validate`}>Validate (dry run)</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" disabled={!file || busy || !res || !res.dry_run} onClick={() => go(false)} data-testid={`import-${kind}-apply`}>Import</Button>
      </div>
      <div className="text-xs text-slate-500 mt-2">{kind === "items"
        ? "Each row becomes a DRAFT item with an auto-generated part number. Exact duplicates are rejected; near-duplicates are skipped unless allowed."
        : "Rows are grouped by parent. A parent without a BOM gets a new draft; an existing draft is replaced. Locked (released) BOMs need an ECN. Self-reference, cycles, bad quantities and unknown parts are rejected."}</div>
      {res && kind === "items" && (
        <div className="mt-3">
          <div className="text-sm mb-2">{res.dry_run ? "Dry run" : "Imported"}: {Object.entries(res.summary).map(([k, v]) => `${v} ${k}`).join(" · ")}</div>
          <div className="max-h-72 overflow-auto"><table className="w-full text-xs"><tbody>
            {res.rows.map((r) => <tr key={r.row} className={`border-b border-slate-50 ${r.status === "error" ? "text-red-700" : r.status === "skipped" ? "text-amber-700" : ""}`}>
              <td className="py-1 pr-2 mono">Row {r.row}</td><td className="pr-2 uppercase">{r.status}</td><td className="pr-2 mono">{r.number}</td><td>{r.messages.join("; ")}</td></tr>)}
          </tbody></table></div>
        </div>
      )}
      {res && kind === "bom" && (
        <div className="mt-3 space-y-2 text-xs">
          {res.row_errors.map((e) => <div key={e.row} className="text-red-700">Row {e.row}: {e.message}</div>)}
          {res.boms.map((b) => <div key={b.parent_no} className={b.errors.length ? "text-red-700" : "text-emerald-700"}>
            <b className="mono">{b.parent_no}</b> · {b.lines} line(s) · {b.action}{b.errors.length ? ` — ${b.errors.join("; ")}` : " — OK"}</div>)}
          {res.note && <div className="text-amber-700">{res.note}</div>}
        </div>
      )}
    </Section>
  );
}

export default function DesignReports() {
  const { me, can, error: meErr } = useDesignMe();
  const initial = new URLSearchParams(window.location.search).get("tab") || "reports";
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-reports-page">
      <PageHeader title="Reports, Audit & Import" subtitle="Registers and KPIs as Excel, the controlled audit trail, and Excel import of items and BOMs" />
      <Tabs defaultValue={initial}>
        <TabsList>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          {can("audit_view") && <TabsTrigger value="audit">Audit trail</TabsTrigger>}
          {can("import") && <TabsTrigger value="import">Excel import</TabsTrigger>}
        </TabsList>
        <TabsContent value="reports"><Reports /></TabsContent>
        <TabsContent value="audit"><Audit /></TabsContent>
        <TabsContent value="import" className="space-y-4"><ImportPanel kind="items" /><ImportPanel kind="bom" /></TabsContent>
      </Tabs>
    </div>
  );
}
